import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ScrollReveal.css';

/**
 * Reveals children with a fade/slide-in when they enter the viewport.
 * Uses IntersectionObserver (no dependencies) and respects reduced motion.
 */
const ScrollReveal = ({
  children,
  className = '',
  as: Tag = 'div',
  once = true,
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  delayMs = 0,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  const style = useMemo(() => {
    return delayMs ? { transitionDelay: `${delayMs}ms` } : undefined;
  }, [delayMs]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If IntersectionObserver is unavailable, show content immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);

  return (
    <Tag
      ref={ref}
      className={`scroll-reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}
      style={style}
    >
      {children}
    </Tag>
  );
};

export default ScrollReveal;

